import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { accountV2Enabled } from '../site-config';
import { AccountV2Service, FollowedPlate } from './account-v2-service';
import { LegacyPlateLists } from './legacy-plate-lists';
import { PendingFavouriteService } from './pending-favourite-service';
import { QuickSignupService } from './quick-signup-service';
import { SessionService } from './session-service';

/**
 * "Track plate": the single tracking system behind every Track button.
 *
 * Backed by account-v2 followed plates (POST /account/followed), which is
 * idempotent, is what the account's Tracked plates pane, the overview count,
 * the homepage and the updates feed read, and is what the hourly listing-diff
 * fan-out notifies. Intent 'tracking' = availability, price, new listings and
 * sale results (daily), editable later from the account.
 *
 * The legacy notify toggle (/userdetails/notify) is NOT used here: it is a GET
 * that adds or removes, so retries and post-login replays can silently untrack.
 *
 * Holds one shared set of tracked registrations so every button on a page
 * (hero row, status panel, Platefolio, new-release cards) agrees, and reloads
 * it whenever the session changes (sign in / sign out).
 *
 * Release hosts (staging, www) use the legacy notify list instead
 * (LegacyPlateLists): the production API has no /account/followed endpoints.
 * Its toggle is only called when the known state says the opposite.
 */
@Injectable({ providedIn: 'root' })
export class TrackPlateService {
  private readonly tracked$ = new BehaviorSubject<Set<string>>(new Set());
  private loadedFor: string | null = null;
  private inFlight = new Set<string>();
  private readonly legacy: LegacyPlateLists | null;

  constructor(
    private account: AccountV2Service,
    private session: SessionService,
    private signup: QuickSignupService,
    private pending: PendingFavouriteService,
    http: HttpClient
  ) {
    this.legacy = accountV2Enabled ? null : new LegacyPlateLists(http);
    this.session.sessionStateChange.subscribe(() => this.refresh(true));
  }

  public static canonical(reg: string | null | undefined): string {
    return (reg || '').replace(/\s+/g, '').toUpperCase();
  }

  /** Emits the current tracked set (loads it on first use when signed in). */
  public get changes(): Observable<Set<string>> {
    this.refresh(false);
    return this.tracked$.asObservable();
  }

  public isTracked(reg: string | null | undefined): boolean {
    return this.tracked$.value.has(TrackPlateService.canonical(reg));
  }

  public isBusy(reg: string | null | undefined): boolean {
    return this.inFlight.has(TrackPlateService.canonical(reg));
  }

  public refresh(force: boolean): void {
    if (!this.session.isLoggedIn()) {
      this.loadedFor = null;
      if (this.tracked$.value.size) this.tracked$.next(new Set());
      return;
    }
    const who = 'session';
    if (!force && this.loadedFor === who) return;
    this.loadedFor = who;
    const regs$: Observable<string[]> = this.legacy
      ? this.legacy.notifies()
      : this.account
          .getFollowed()
          .pipe(map((rows: FollowedPlate[]) => rows.map((r) => TrackPlateService.canonical(r.registration))));
    regs$.pipe(catchError(() => of([] as string[]))).subscribe((regs) => this.tracked$.next(new Set(regs)));
  }

  /**
   * Track a plate. Signed out: opens the sign-in box and completes after
   * sign-in; for a full-page sign-in (Apple) the intent is stored and replayed
   * by the login page via {@link commit}.
   */
  public track(reg: string, display?: string, done?: (ok: boolean) => void): void {
    const r = TrackPlateService.canonical(reg);
    if (!r) return;
    this.signup.quickSignup(
      `track ${display || r}`,
      undefined,
      (ok: boolean) => {
        if (!ok) return done?.(false);
        this.commit(r).subscribe((res) => done?.(res));
      },
      () => {},
      { action: 'track', registration: r }
    );
  }

  /** Signed-in add. Idempotent: an existing row is kept with its alert settings. */
  public commit(reg: string): Observable<boolean> {
    const r = TrackPlateService.canonical(reg);
    if (!r || this.inFlight.has(r)) return of(false);
    if (this.legacy && this.isTracked(r)) return of(true);
    this.inFlight.add(r);
    const call$ = this.legacy
      ? this.legacy.toggleNotify(r)
      : this.account.follow(r, 'tracking').pipe(map(() => true), catchError(() => of(false)));
    return call$.pipe(
      tap((ok) => {
        this.inFlight.delete(r);
        if (ok) this.tracked$.next(new Set(this.tracked$.value).add(r));
      })
    );
  }

  public untrack(reg: string): Observable<boolean> {
    const r = TrackPlateService.canonical(reg);
    if (!r || this.inFlight.has(r)) return of(false);
    if (this.legacy && !this.isTracked(r)) return of(true);
    this.inFlight.add(r);
    const call$ = this.legacy
      ? this.legacy.toggleNotify(r)
      : this.account.unfollow(r).pipe(map(() => true), catchError(() => of(false)));
    return call$.pipe(
      tap((ok) => {
        this.inFlight.delete(r);
        if (ok) {
          const next = new Set(this.tracked$.value);
          next.delete(r);
          this.tracked$.next(next);
        }
      })
    );
  }

  /** Replays a 'track' intent stored before a full-page sign-in redirect. */
  public replayPending(): boolean {
    const p = this.pending.get();
    if (!p || p.action !== 'track') return false;
    this.pending.clear();
    this.commit(p.registration).subscribe();
    return true;
  }
}
